import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Save, Upload, Building2, Phone, Mail, MapPin, FileText, CreditCard, Bold, Heading1, Heading2, List, Bell, Send, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { MorningDigestCard } from '@/components/MorningDigestCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function RichTextarea({ value, onChange, placeholder, className, 'data-testid': testId }) {
  const ref = useRef(null);

  const insertPrefix = (prefix) => {
    const ta = ref.current;
    const start = ta.selectionStart;
    const text = value || '';
    // Find the start of the current line
    const lineStart = text.lastIndexOf('\n', start - 1) + 1;
    const lineEnd = text.indexOf('\n', start);
    const currentLine = text.slice(lineStart, lineEnd === -1 ? text.length : lineEnd);
    // Toggle: remove prefix if already present, add otherwise
    if (currentLine.startsWith(prefix)) {
      const newText = text.slice(0, lineStart) + currentLine.slice(prefix.length) + text.slice(lineEnd === -1 ? text.length : lineEnd);
      onChange(newText);
      setTimeout(() => { ta.selectionStart = ta.selectionEnd = start - prefix.length; }, 0);
    } else {
      // Remove any existing heading/bullet prefix first
      const cleaned = currentLine.replace(/^(#{1,2}\s|- )/, '');
      const newText = text.slice(0, lineStart) + prefix + cleaned + text.slice(lineEnd === -1 ? text.length : lineEnd);
      onChange(newText);
      const oldPrefixLen = currentLine.length - cleaned.length;
      setTimeout(() => { ta.selectionStart = ta.selectionEnd = start - oldPrefixLen + prefix.length; }, 0);
    }
  };

  const toggleBold = () => {
    const ta = ref.current;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const text = value || '';
    if (start !== end) {
      const selected = text.slice(start, end);
      if (text.slice(start - 2, start) === '**' && text.slice(end, end + 2) === '**') {
        onChange(text.slice(0, start - 2) + selected + text.slice(end + 2));
        setTimeout(() => { ta.selectionStart = start - 2; ta.selectionEnd = end - 2; }, 0);
      } else {
        onChange(text.slice(0, start) + '**' + selected + '**' + text.slice(end));
        setTimeout(() => { ta.selectionStart = start + 2; ta.selectionEnd = end + 2; }, 0);
      }
    } else {
      onChange(text.slice(0, start) + '****' + text.slice(end));
      setTimeout(() => { ta.selectionStart = start + 2; ta.selectionEnd = start + 2; }, 0);
    }
  };

  const handleKeyDown = (e) => {
    if (!(e.ctrlKey || e.metaKey)) return;
    if (e.key === 'b') { e.preventDefault(); toggleBold(); }
    else if (e.key === '1') { e.preventDefault(); insertPrefix('# '); }
    else if (e.key === '2') { e.preventDefault(); insertPrefix('## '); }
    else if (e.key === 'l') { e.preventDefault(); insertPrefix('- '); }
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-0.5 mb-1">
        <button type="button" onClick={() => { ref.current?.focus(); insertPrefix('# '); }} title="Heading (Ctrl+1)"
          className="p-1 rounded hover:bg-stone-100 text-stone-500 hover:text-stone-700 transition-colors">
          <Heading1 className="w-3.5 h-3.5" />
        </button>
        <button type="button" onClick={() => { ref.current?.focus(); insertPrefix('## '); }} title="Subheading (Ctrl+2)"
          className="p-1 rounded hover:bg-stone-100 text-stone-500 hover:text-stone-700 transition-colors">
          <Heading2 className="w-3.5 h-3.5" />
        </button>
        <button type="button" onClick={() => { ref.current?.focus(); toggleBold(); }} title="Bold (Ctrl+B)"
          className="p-1 rounded hover:bg-stone-100 text-stone-500 hover:text-stone-700 transition-colors">
          <Bold className="w-3.5 h-3.5" />
        </button>
        <button type="button" onClick={() => { ref.current?.focus(); insertPrefix('- '); }} title="Bullet (Ctrl+L)"
          className="p-1 rounded hover:bg-stone-100 text-stone-500 hover:text-stone-700 transition-colors">
          <List className="w-3.5 h-3.5" />
        </button>
        <span className="text-[9px] text-stone-400 ml-1">Ctrl+1 H1 · Ctrl+2 H2 · Ctrl+B Bold · Ctrl+L Bullet</span>
      </div>
      <textarea
        ref={ref}
        data-testid={testId}
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
      />
    </div>
  );
}

export default function BusinessSettings() {
  const [settings, setSettings] = useState(null);
  const [notifSettings, setNotifSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [notifDirty, setNotifDirty] = useState(false);
  const [activeTab, setActiveTab] = useState('business');
  const [testing, setTesting] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const [bizRes, notifRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/business-settings`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/notification-settings`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setSettings(bizRes.data);
      setNotifSettings(notifRes.data);
    } catch {
      setSettings({
        business_name: '', address: '', phone: '', email: '',
        logo_base64: '', remarks: '', inclusions: '', exclusions: '', payment_terms: '', greeting: '', footer: ''
      });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  useEffect(() => {
    if (!dirty && !notifDirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty, notifDirty]);

  const update = (key, val) => {
    setSettings(prev => ({ ...prev, [key]: val }));
    setDirty(true);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) { toast.error('Logo must be under 500KB'); return; }
    const reader = new FileReader();
    reader.onload = () => { update('logo_base64', reader.result); };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (activeTab === 'business') {
        await axios.put(`${BACKEND_URL}/api/business-settings`, settings, { withCredentials: true });
        setDirty(false);
      } else {
        await axios.put(`${BACKEND_URL}/api/notification-settings`, notifSettings, { withCredentials: true });
        setNotifDirty(false);
      }
      toast.success('Settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const updateNotif = (key, val) => {
    setNotifSettings(prev => ({ ...prev, [key]: val }));
    setNotifDirty(true);
  };
  const updateEvent = (eventKey, field, val) => {
    setNotifSettings(prev => ({
      ...prev,
      events: { ...prev.events, [eventKey]: { ...prev.events[eventKey], [field]: val } }
    }));
    setNotifDirty(true);
  };
  const toggleRecipient = (eventKey, recipient) => {
    setNotifSettings(prev => {
      const current = prev.events[eventKey]?.recipients || [];
      const updated = current.includes(recipient)
        ? current.filter(r => r !== recipient)
        : [...current, recipient];
      return {
        ...prev,
        events: { ...prev.events, [eventKey]: { ...prev.events[eventKey], recipients: updated } }
      };
    });
    setNotifDirty(true);
  };
  const sendTestNotification = async () => {
    setTesting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/notification-settings/test`, {}, { withCredentials: true });
      toast.success('Test notification sent to admin WhatsApp!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test failed');
    } finally { setTesting(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-700"></div>
    </div>
  );

  const isDirty = activeTab === 'business' ? dirty : notifDirty;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Business Settings</h1>
          <p className="text-xs text-stone-500 mt-1">Configure your business details and notifications</p>
        </div>
        <Button data-testid="save-settings-btn" size="sm" onClick={handleSave} disabled={saving}
          className={`text-xs h-8 ${isDirty ? 'bg-red-600 hover:bg-red-700 animate-pulse' : 'bg-amber-700 hover:bg-amber-800'} text-white`}>
          <Save className="w-3 h-3 mr-1" /> {saving ? 'Saving...' : isDirty ? 'Unsaved Changes' : 'Save Settings'}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-stone-200">
        <button data-testid="tab-business" onClick={() => setActiveTab('business')}
          className={`px-4 py-2 text-xs font-semibold border-b-2 transition-colors ${activeTab === 'business' ? 'border-amber-700 text-amber-800' : 'border-transparent text-stone-500 hover:text-stone-700'}`}>
          <Building2 className="w-3.5 h-3.5 inline mr-1.5" />Business & PDF
        </button>
        <button data-testid="tab-notifications" onClick={() => setActiveTab('notifications')}
          className={`px-4 py-2 text-xs font-semibold border-b-2 transition-colors ${activeTab === 'notifications' ? 'border-amber-700 text-amber-800' : 'border-transparent text-stone-500 hover:text-stone-700'}`}>
          <Bell className="w-3.5 h-3.5 inline mr-1.5" />WhatsApp Notifications
        </button>
      </div>

      {activeTab === 'notifications' && notifSettings && (
        <div className="space-y-6">
          <MorningDigestCard />
          {/* Twilio Configuration */}
          <div className="bg-white rounded-xl border border-stone-200 p-5 space-y-4">
            <h2 className="text-sm font-bold text-stone-800 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-green-600" /> Twilio WhatsApp Configuration</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-stone-600">Twilio Account SID</Label>
                <Input data-testid="notif-twilio-sid" value={notifSettings.twilio_sid || ''} onChange={e => updateNotif('twilio_sid', e.target.value)} placeholder="ACxxxxxxxxx" className="mt-1 border-stone-200 text-xs font-mono" />
              </div>
              <div>
                <Label className="text-xs text-stone-600">Twilio Auth Token</Label>
                <Input data-testid="notif-twilio-token" type="password" value={notifSettings.twilio_token || ''} onChange={e => updateNotif('twilio_token', e.target.value)} placeholder="xxxxxxxxx" className="mt-1 border-stone-200 text-xs font-mono" />
              </div>
              <div>
                <Label className="text-xs text-stone-600">WhatsApp "From" Number</Label>
                <Input data-testid="notif-whatsapp-from" value={notifSettings.whatsapp_from || ''} onChange={e => updateNotif('whatsapp_from', e.target.value)} placeholder="+17752578798" className="mt-1 border-stone-200 text-xs" />
              </div>
              <div>
                <Label className="text-xs text-stone-600">Admin WhatsApp Number</Label>
                <Input data-testid="notif-admin-whatsapp" value={notifSettings.admin_whatsapp || ''} onChange={e => updateNotif('admin_whatsapp', e.target.value)} placeholder="+917418086026" className="mt-1 border-stone-200 text-xs" />
              </div>
              <div>
                <Label className="text-xs text-stone-600">Batch Window (minutes)</Label>
                <p className="text-[9px] text-stone-400">Status updates within this window are grouped into one message</p>
                <Input data-testid="notif-batch-window" type="number" min="1" max="30" value={notifSettings.batch_window_minutes || 5} onChange={e => updateNotif('batch_window_minutes', parseInt(e.target.value) || 5)} className="mt-1 border-stone-200 text-xs w-20" />
              </div>
            </div>
            <Button data-testid="test-notification-btn" size="sm" variant="outline" onClick={sendTestNotification} disabled={testing}
              className="text-xs border-green-200 text-green-700 hover:bg-green-50">
              <Send className="w-3 h-3 mr-1.5" /> {testing ? 'Sending...' : 'Send Test Notification'}
            </Button>
          </div>

          {/* Notification Events */}
          <div className="bg-white rounded-xl border border-stone-200 p-5 space-y-4">
            <h2 className="text-sm font-bold text-stone-800 flex items-center gap-2"><Bell className="w-4 h-4 text-amber-700" /> Notification Events</h2>
            <p className="text-[10px] text-stone-500">Toggle events on/off, customize message templates, and choose who receives each notification.</p>
            <div className="space-y-4">
              {notifSettings.event_metadata && Object.entries(notifSettings.event_metadata).map(([eventKey, meta]) => {
                const event = notifSettings.events?.[eventKey] || { enabled: false, template: meta.default_template || '', recipients: meta.default_recipients || [] };
                return (
                  <div key={eventKey} data-testid={`notif-event-${eventKey}`} className={`p-4 rounded-xl border transition-colors ${event.enabled ? 'bg-white border-green-200' : 'bg-stone-50 border-stone-200 opacity-60'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <span className="text-sm font-semibold text-stone-800">{meta.label}</span>
                        <p className="text-[10px] text-stone-500">{meta.description}</p>
                      </div>
                      <Switch data-testid={`notif-toggle-${eventKey}`} checked={event.enabled} onCheckedChange={v => updateEvent(eventKey, 'enabled', v)} />
                    </div>
                    {event.enabled && (
                      <div className="space-y-3 mt-3">
                        <div>
                          <Label className="text-[10px] text-stone-500">Message Template</Label>
                          <textarea data-testid={`notif-template-${eventKey}`} value={event.template || ''} onChange={e => updateEvent(eventKey, 'template', e.target.value)}
                            rows={5}
                            className="mt-1 w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-xs font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-amber-500/30" />
                          <p className="text-[9px] text-stone-400 mt-1">Press Enter for a new line. Variables: {meta.variables.map(v => `{${v}}`).join(', ')}</p>
                        </div>
                        <div>
                          <Label className="text-[10px] text-stone-500">Send to</Label>
                          <div className="flex gap-2 mt-1">
                            {['admin', 'vendor', 'staff', 'customer'].map(r => (
                              <button key={r} onClick={() => toggleRecipient(eventKey, r)}
                                className={`px-2.5 py-1 rounded-full text-[10px] font-semibold border transition-colors ${event.recipients?.includes(r) ? 'bg-green-100 border-green-300 text-green-800' : 'bg-stone-50 border-stone-200 text-stone-400'}`}>
                                {r.charAt(0).toUpperCase() + r.slice(1)}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'business' && (<>

      {/* Business Info */}
      <div className="bg-white rounded-xl border border-stone-200 p-5 space-y-4">
        <h2 className="text-sm font-bold text-stone-800 flex items-center gap-2"><Building2 className="w-4 h-4 text-amber-700" /> Business Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-stone-600">Business Name</Label>
            <Input data-testid="settings-business-name" value={settings.business_name} onChange={e => update('business_name', e.target.value)} placeholder="Your business name" className="mt-1 border-stone-200" />
          </div>
          <div>
            <Label className="text-xs text-stone-600 flex items-center gap-1"><Phone className="w-3 h-3" /> Phone</Label>
            <Input data-testid="settings-phone" value={settings.phone} onChange={e => update('phone', e.target.value)} placeholder="Phone number" className="mt-1 border-stone-200" />
          </div>
          <div>
            <Label className="text-xs text-stone-600 flex items-center gap-1"><Mail className="w-3 h-3" /> Email</Label>
            <Input data-testid="settings-email" value={settings.email} onChange={e => update('email', e.target.value)} placeholder="Email address" className="mt-1 border-stone-200" />
          </div>
          <div>
            <Label className="text-xs text-stone-600 flex items-center gap-1"><MapPin className="w-3 h-3" /> Address</Label>
            <Input data-testid="settings-address" value={settings.address} onChange={e => update('address', e.target.value)} placeholder="Full address" className="mt-1 border-stone-200" />
          </div>
        </div>

        {/* Logo */}
        <div>
          <Label className="text-xs text-stone-600">Logo (max 500KB, PNG/JPG)</Label>
          <div className="flex items-center gap-4 mt-1">
            {settings.logo_base64 && (
              <img src={settings.logo_base64} alt="Logo" className="h-14 w-auto object-contain rounded border border-stone-200 p-1" />
            )}
            <label className="cursor-pointer">
              <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleLogoUpload} className="hidden" />
              <span data-testid="settings-logo-upload" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-amber-300 bg-amber-50/50 text-xs font-medium text-amber-800 hover:bg-amber-50 transition-colors">
                <Upload className="w-3.5 h-3.5" /> {settings.logo_base64 ? 'Change Logo' : 'Upload Logo'}
              </span>
            </label>
            {settings.logo_base64 && (
              <button onClick={() => update('logo_base64', '')} className="text-xs text-red-500 hover:text-red-700">Remove</button>
            )}
          </div>
        </div>
      </div>

      {/* Greeting / Cover Note */}
      <div className="bg-white rounded-xl border border-stone-200 p-5 space-y-4">
        <h2 className="text-sm font-bold text-stone-800 flex items-center gap-2"><FileText className="w-4 h-4 text-amber-700" /> Greeting / Cover Note</h2>
        <div>
          <Label className="text-xs text-stone-600">This text appears at the top of every PDF, below the header and above the quotation table</Label>
          <RichTextarea data-testid="settings-greeting" value={settings.greeting} onChange={v => update('greeting', v)}
            placeholder={"Dear Customer,\nThank you for choosing Nirmaan Interiors..."}
            className="mt-1 w-full border border-stone-200 rounded-lg px-3 py-2 text-sm min-h-[100px] focus:outline-none focus:ring-2 focus:ring-amber-200" />
        </div>
      </div>

      {/* Remarks & Inclusions/Exclusions */}
      <div className="bg-white rounded-xl border border-stone-200 p-5 space-y-4">
        <h2 className="text-sm font-bold text-stone-800 flex items-center gap-2"><FileText className="w-4 h-4 text-amber-700" /> Quotation Remarks</h2>
        <div>
          <Label className="text-xs text-stone-600">General Remarks / Recommendations</Label>
          <RichTextarea data-testid="settings-remarks" value={settings.remarks} onChange={v => update('remarks', v)}
            placeholder="Any general notes or recommendations for the customer..."
            className="mt-1 w-full border border-stone-200 rounded-lg px-3 py-2 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-amber-200" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-stone-600">Inclusions (one per line)</Label>
            <RichTextarea data-testid="settings-inclusions" value={settings.inclusions} onChange={v => update('inclusions', v)}
              placeholder={"Plywood\nLaminates\nHardware\nLabour\nTransportation"}
              className="mt-1 w-full border border-stone-200 rounded-lg px-3 py-2 text-sm min-h-[120px] focus:outline-none focus:ring-2 focus:ring-amber-200" />
          </div>
          <div>
            <Label className="text-xs text-stone-600">Exclusions (one per line)</Label>
            <RichTextarea data-testid="settings-exclusions" value={settings.exclusions} onChange={v => update('exclusions', v)}
              placeholder={"Electrical work\nPainting\nFalse ceiling\nCivil work"}
              className="mt-1 w-full border border-stone-200 rounded-lg px-3 py-2 text-sm min-h-[120px] focus:outline-none focus:ring-2 focus:ring-amber-200" />
          </div>
        </div>
      </div>

      {/* Payment Terms */}
      <div className="bg-white rounded-xl border border-stone-200 p-5 space-y-4">
        <h2 className="text-sm font-bold text-stone-800 flex items-center gap-2"><CreditCard className="w-4 h-4 text-amber-700" /> Payment Terms</h2>
        <div>
          <Label className="text-xs text-stone-600">Payment Terms (one per line)</Label>
          <RichTextarea data-testid="settings-payment-terms" value={settings.payment_terms} onChange={v => update('payment_terms', v)}
            placeholder={"50% advance on confirmation\n40% on material delivery\n10% on project completion"}
            className="mt-1 w-full border border-stone-200 rounded-lg px-3 py-2 text-sm min-h-[120px] focus:outline-none focus:ring-2 focus:ring-amber-200" />
        </div>
      </div>

      {/* Footer / Closing Statement */}
      <div className="bg-white rounded-xl border border-stone-200 p-5 space-y-4">
        <h2 className="text-sm font-bold text-stone-800 flex items-center gap-2"><FileText className="w-4 h-4 text-amber-700" /> Closing Statement</h2>
        <div>
          <Label className="text-xs text-stone-600">Appears at the very bottom of the PDF as a closing note</Label>
          <RichTextarea data-testid="settings-footer" value={settings.footer} onChange={v => update('footer', v)}
            placeholder={"We look forward to working with you!\nFor queries, reach us at 9279304050"}
            className="mt-1 w-full border border-stone-200 rounded-lg px-3 py-2 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-amber-200" />
        </div>
      </div>
      </>)}
    </div>
  );
}
